import { contrastRatio, type ReportBrand } from "@klantpunt/shared";
import type { ReportStyle } from "./buildReport";
import { parseAltLogoSvg, parseKlantpuntLogoSvg, parseLogoSvg } from "./logo";

/** Logo-bronnen (SVG-tekst); in de browser via `?raw`, in tests van schijf. */
export interface StyleSources {
  pandwachtersCover: string;
  pandwachtersPage: string;
  klantpunt: string;
}

/** Eigen huisstijl van de organisatie bovenop `report_brand` (docs/BRAND.md). */
export interface CustomBrand {
  /** Naam van de organisatie: voettekst en PDF-eigenschappen. */
  name: string;
  accent: string | null;
  logo: ReportStyle["customLogo"] | null;
}

/** Huisstijl van het rapport per organisatie-instelling `report_brand`, eventueel met eigen logo en kleur. */
export function reportStyle(brand: ReportBrand, src: StyleSources, custom?: CustomBrand): ReportStyle {
  const base = baseStyle(brand, src);
  if (!custom || (!custom.accent && !custom.logo)) return base;
  const accent = custom.accent ?? base.accent;
  return {
    ...base,
    custom: true,
    onDark: contrastRatio(accent, base.dark) >= 3 ? accent : "#FFFFFF",
    name: custom.name,
    site: custom.name,
    slogan: undefined,
    accent,
    ...(custom.logo ? { customLogo: custom.logo } : {}),
    // In de stijl van een klant: klein "gemaakt met klantpunt.io" in de voettekst.
    poweredBy: true,
  };
}

function baseStyle(brand: ReportBrand, src: StyleSources): ReportStyle {
  if (brand === "pandwachters") {
    return {
      key: "pandwachters",
      name: "Pandwachters",
      accent: "#EC663A",
      dark: "#05080D",
      site: "pandwachters.nl",
      slogan: "Zet een punt achter gedoe",
      bullet: "+",
      logoCover: parseLogoSvg(src.pandwachtersCover),
      coverLogoW: 190,
      logoPage: parseAltLogoSvg(src.pandwachtersPage),
      pageLogoW: 78,
      poweredBy: true,
    };
  }
  const kp = parseKlantpuntLogoSvg(src.klantpunt);
  return {
    key: "klantpunt",
    name: "Klantpunt",
    accent: "#A93BF5",
    dark: "#1C191C",
    site: "klantpunt.io",
    bullet: "•",
    logoCover: kp,
    coverLogoW: 240,
    logoPage: kp,
    pageLogoW: 96,
    poweredBy: false,
  };
}
